import { devLog } from "./devLog";
// Shared bulk-fetch caches for text and image slots.
// One GET on first need; every component subscribes to its slot id.

const API = process.env.REACT_APP_BACKEND_URL || "";

// ── Helpers ─────────────────────────────────────────────────────────────
export const resolveUrl = (url) => {
  if (!url) return null;
  if (typeof url !== "string") return null;
  if (url.startsWith("/api/")) return `${API}${url}`;
  return url;
};

// ── TEXT cache ──────────────────────────────────────────────────────────
const textCache = {
  ready: false,
  loading: null,
  values: new Map(),               // slot_id → { en, fr, es }
  subscribers: new Map(),          // slot_id → Set<callback>
};

// ── localStorage persistence ────────────────────────────────────────────
// Synchronously hydrate the in-memory cache from localStorage on module
// load so the very first paint can apply CMS overrides — no flash of
// hardcoded default text on subsequent visits. The fresh server payload
// is still fetched in the background (`ensureTextLoaded`) and replaces
// stale values once it arrives.
//
// COLD-CACHE PATH: index.html runs a synchronous boot script that
// hides the document (`html.cms-loading { visibility:hidden }`) and
// kicks off an early `/api/text_slots` fetch into
// `window.__CMS_INITIAL_FETCH__`, populating `window.__CMS_INITIAL_STATE__`
// when it resolves. We consume both here so React never paints a
// hardcoded fallback string on cold cache.
const LS_KEY = "xaluca-text-slots-v1";
const LS_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

const unhideDocument = () => {
  if (typeof document === "undefined") return;
  if (document.documentElement.classList.contains("cms-loading")) {
    document.documentElement.classList.remove("cms-loading");
  }
  if (typeof window !== "undefined") {
    // Clear all loading-related timeouts that might still be pending.
    const timeoutKeys = [
      "__CMS_UNHIDE_TIMEOUT__",
      "__CMS_ERROR_OVERLAY_TIMEOUT__",
      "__CMS_LOADING_SPLASH_TIMEOUT__",
      "__CMS_HARD_FAIL_TIMEOUT__",
    ];
    for (const key of timeoutKeys) {
      if (window[key]) {
        try { window.clearTimeout(window[key]); } catch (_) { /* noop */ }
        window[key] = null;
      }
    }
  }
  // Fade out and remove the loading splash with a tasteful transition
  // so the reveal doesn't feel jarring. The splash auto-dismisses on
  // first successful CMS paint.
  if (typeof document !== "undefined") {
    const splash = document.getElementById("__cms-loading-splash__");
    if (splash) {
      splash.style.opacity = "0";
      splash.style.pointerEvents = "none";
      setTimeout(() => {
        if (splash.parentNode) splash.parentNode.removeChild(splash);
      }, 360);
    }
    // Legacy: also remove the older overlay if it somehow ended up in
    // the DOM (defensive — should never happen after this refactor).
    const overlay = document.getElementById("__cms-connection-overlay__");
    if (overlay && overlay.parentNode) overlay.parentNode.removeChild(overlay);
    const rootEl = document.getElementById("root");
    if (rootEl && rootEl.style.visibility === "hidden") rootEl.style.visibility = "";
  }
};

const hydrateFromInitialState = () => {
  // index.html's boot script may have stashed the full payload here
  // before React even started parsing — pick it up synchronously.
  try {
    if (typeof window === "undefined") return 0;
    const initial = window.__CMS_INITIAL_STATE__;
    if (!initial || !initial.slots) return 0;
    let count = 0;
    for (const [slot, vals] of Object.entries(initial.slots)) {
      if (vals && typeof vals === "object") {
        textCache.values.set(slot, vals);
        count += 1;
      }
    }
    // When the payload is page-scoped (boot script optimisation, see
    // index.html) we MUST NOT flip `ready` — that would short-circuit
    // `ensureTextLoaded()` and the follow-up full fetch would never
    // fire, leaving SPA navigation cache-cold. Only flip `ready` when
    // the response was the unfiltered full set.
    if (count > 0 && !initial.page) textCache.ready = true;
    return count;
  } catch {
    return 0;
  }
};

const hydrateFromLocalStorage = () => {
  try {
    if (typeof window === "undefined") return false;
    const raw = window.localStorage.getItem(LS_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.ts !== "number") return false;
    if (Date.now() - parsed.ts > LS_MAX_AGE_MS) return false;
    const slots = parsed.slots || {};
    let count = 0;
    for (const [slot, vals] of Object.entries(slots)) {
      if (vals && typeof vals === "object") {
        textCache.values.set(slot, vals);
        count += 1;
      }
    }
    return count > 0;
  } catch {
    return false;
  }
};

let textCachePersistTimer = null;
const persistToLocalStorage = () => {
  // Debounce so a burst of edits in Edit Mode doesn't thrash localStorage.
  if (textCachePersistTimer) clearTimeout(textCachePersistTimer);
  textCachePersistTimer = setTimeout(() => {
    try {
      if (typeof window === "undefined") return;
      const out = {};
      for (const [slot, vals] of textCache.values) out[slot] = vals;
      const payload = JSON.stringify({ ts: Date.now(), slots: out });
      window.localStorage.setItem(LS_KEY, payload);
    } catch {
      // Quota exceeded / private-mode — silently skip.
    }
  }, 800);
};

// Run synchronously at import time so the first React render already
// sees the warm cache. Prefer the inline boot payload from index.html
// (cold-cache first-time visitors) and fall back to localStorage
// (returning visitors). If neither has data, `ensureTextLoaded` below
// awaits the in-flight fetch kicked off by the boot script.
const initialStateCount = hydrateFromInitialState();
if (initialStateCount === 0) hydrateFromLocalStorage();

const notifyText = (slot) => {
  const subs = textCache.subscribers.get(slot);
  if (!subs) return;
  const value = textCache.values.get(slot);
  for (const cb of subs) cb(value);
};

export const unhideDocumentForCms = () => unhideDocument();

// Returns true once the CMS text slot cache has data (either from
// the initial-state boot payload, localStorage, or a successful fetch).
// AutoTextEditor uses this to gate the cold-cache unhide so we never
// reveal the page before CMS overrides have been applied.
export const isTextCacheReady = () => textCache.ready;
export const hasAnyCachedText = () => textCache.values.size > 0;

// ── Full background refresh ───────────────────────────────────────────
// On cold cache the boot script only fetches slots for the current
// route (~25 KB gzipped instead of ~666 KB for the full set), so the
// loading splash disappears in ~50-100 ms. The trade-off is that SPA
// navigation to OTHER pages would then encounter cache misses and the
// hardcoded source copy would briefly paint.
//
// To eliminate that trade-off we kick off ONE background fetch of the
// complete payload as soon as the initial page-scoped fetch resolves.
// The full payload is merged into `textCache.values` (page-scoped
// values are not overwritten because they're already correct) and
// persisted to localStorage so the next visit gets every page's slots
// warm-cached before first paint.
let fullRefreshPromise = null;

const ensureFullTextLoaded = () => {
  if (fullRefreshPromise) return fullRefreshPromise;
  fullRefreshPromise = (async () => {
    try {
      const res = await fetch(`${API}/api/text_slots`, { cache: "no-store" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const incoming = json?.slots || {};
      let added = 0;
      for (const [slot, vals] of Object.entries(incoming)) {
        // Don't clobber values the editor mutated optimistically since
        // the initial fetch — merge missing keys only when the slot is
        // already present.
        const existing = textCache.values.get(slot);
        if (!existing) {
          textCache.values.set(slot, vals || {});
          added += 1;
        } else {
          const merged = { ...vals, ...existing };
          textCache.values.set(slot, merged);
        }
      }
      persistToLocalStorage();
      if (added > 0) {
        for (const slot of textCache.subscribers.keys()) notifyText(slot);
      }
    } catch (err) {
      devLog.debug("[CMS] full text refresh failed", err?.message);
      // Clear so a future call can retry.
      fullRefreshPromise = null;
    }
  })();
  return fullRefreshPromise;
};

export { ensureFullTextLoaded };

// Schedules the full refresh on the next idle tick — gives the browser
// time to commit the first paint and any pending React work before
// spending bandwidth on the 666 KB payload.
const scheduleFullTextRefresh = () => {
  if (fullRefreshPromise) return;
  const kick = () => { ensureFullTextLoaded(); };
  if (typeof window !== "undefined" && typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(kick, { timeout: 1500 });
  } else if (typeof window !== "undefined") {
    window.setTimeout(kick, 200);
  }
};

export const ensureTextLoaded = () => {
  if (textCache.ready) {
    return Promise.resolve();
  }
  if (textCache.loading) return textCache.loading;

  // Reuse the in-flight fetch kicked off by index.html's boot script
  // (cold-cache path). Avoids a duplicate network round-trip.
  const earlyFetch =
    typeof window !== "undefined" ? window.__CMS_INITIAL_FETCH__ : null;

  if (earlyFetch && typeof earlyFetch.then === "function") {
    textCache.loading = earlyFetch
      .then((json) => {
        const payload = json || window.__CMS_INITIAL_STATE__ || { slots: {} };
        for (const [slot, vals] of Object.entries(payload.slots || {})) {
          textCache.values.set(slot, vals || {});
        }
        // The boot script only fetched the current page's slots
        // (~25 KB instead of ~666 KB). Don't persist this partial map
        // to localStorage — `ensureFullTextLoaded()` will follow up
        // in the background with the complete payload and persist
        // that, so the NEXT visit (warm cache) gets every page's
        // slots ready before first paint.
      })
      .catch((err) => {
        devLog.debug("[CMS] early bootstrap fetch failed", err?.message);
      })
      .finally(() => {
        textCache.ready = true;
        textCache.loading = null;
        // NOTE: don't unhide here — AutoTextEditor will unhide AFTER
        // applyOverrides() in the same animation frame so the user
        // never sees a frame of hardcoded text between unhide and
        // override.
        for (const slot of textCache.subscribers.keys()) notifyText(slot);
        // Schedule the full background fetch so SPA navigation to
        // other pages has its slots ready without another round-trip.
        scheduleFullTextRefresh();
      });
    return textCache.loading;
  }

  textCache.loading = (async () => {
    try {
      const res = await fetch(`${API}/api/text_slots`, { cache: "no-store" });
      const json = await res.json();
      for (const [slot, vals] of Object.entries(json.slots || {})) {
        textCache.values.set(slot, vals || {});
      }
      // Cache the full payload so the next visit gets a flicker-free
      // first paint, hydrated synchronously before any React render.
      persistToLocalStorage();
    } catch (err) {
      devLog.debug("[CMS] text bulk load failed", err?.message);
    }
    textCache.ready = true;
    textCache.loading = null;
    // NOTE: don't unhide here — see comment above.
    for (const slot of textCache.subscribers.keys()) notifyText(slot);
  })();
  return textCache.loading;
};

export const subscribeText = (slot, cb) => {
  if (!textCache.subscribers.has(slot)) textCache.subscribers.set(slot, new Set());
  textCache.subscribers.get(slot).add(cb);
  return () => textCache.subscribers.get(slot)?.delete(cb);
};

export const getCachedText = (slot) => textCache.values.get(slot);

export const persistText = async (slot, values) => {
  // Per-language save with backend-side auto-translation:
  //
  //   1. We send ONLY the language(s) the editor explicitly changed.
  //      The backend treats whichever language was passed in as the
  //      "source", saves it immediately, and fans out a Claude-powered
  //      native translation to the other two languages in the background.
  //
  //   2. Locally we update the cache optimistically for the edited
  //      language only — the existing values for the other languages
  //      stay untouched, so the rest of the site keeps rendering.
  //
  //   3. After ~2.5 s we re-fetch the slot to pick up the freshly
  //      translated values for the languages we didn't touch.
  const incoming = Object.fromEntries(
    Object.entries(values || {}).filter(([, v]) => v !== undefined && v !== null)
  );
  if (!Object.keys(incoming).length) return;

  // Optimistic cache update — only overwrite the keys we changed.
  const previous = textCache.values.get(slot) || {};
  const merged = { ...previous, ...incoming };
  textCache.values.set(slot, merged);
  notifyText(slot);

  await fetch(`${API}/api/text_slots/${encodeURIComponent(slot)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      values: incoming,
      page: typeof window !== "undefined" ? window.location.pathname : null,
    }),
    cache: "no-store",
  });

  // Pull the auto-translated siblings shortly after — gives the
  // Claude background job time to finish (~1-2 s end-to-end).
  window.setTimeout(async () => {
    try {
      const res = await fetch(
        `${API}/api/text_slots/${encodeURIComponent(slot)}`,
        { cache: "no-store" }
      );
      if (!res.ok) return;
      const json = await res.json();
      const fresh = json?.values || {};
      const existing = textCache.values.get(slot) || {};
      textCache.values.set(slot, { ...existing, ...fresh });
      notifyText(slot);
      persistToLocalStorage();
    } catch (err) {
      devLog.debug("[CMS] refresh after translate failed", err?.message);
    }
  }, 2500);
};

// ── STAGED edits ────────────────────────────────────────────────────────
// In inline Edit Mode the editor types freely without triggering a
// translation each time they blur a paragraph. Each edit is "staged"
// here (optimistic cache update + remembered original) and only flushed
// to the backend when the editor hits the floating Save button.
const staged = {
  entries: new Map(), // slot_id → { original: {...}, current: {...} }
  listeners: new Set(),
};

const notifyStaged = () => {
  for (const cb of staged.listeners) cb(staged.entries.size);
};

export const subscribeStagedCount = (cb) => {
  staged.listeners.add(cb);
  cb(staged.entries.size);
  return () => staged.listeners.delete(cb);
};

export const getStagedCount = () => staged.entries.size;

export const stageText = (slot, values) => {
  const incoming = Object.fromEntries(
    Object.entries(values || {}).filter(([, v]) => v !== undefined && v !== null)
  );
  if (!Object.keys(incoming).length) return;
  const prev = textCache.values.get(slot) || {};
  if (!staged.entries.has(slot)) {
    staged.entries.set(slot, { original: { ...prev }, current: { ...prev } });
  }
  const entry = staged.entries.get(slot);
  entry.current = { ...entry.current, ...incoming };
  // Optimistic cache update so the editor (and live page) reflect the
  // pending change immediately.
  textCache.values.set(slot, { ...prev, ...incoming });
  notifyText(slot);
  notifyStaged();
};

export const discardStagedTexts = () => {
  // Revert both the in-memory cache AND the live DOM for any element
  // that was edited inline. We do this by walking `[data-cms-auto-text="slot"]`
  // elements and resetting their visible text to the original stamp
  // (`data-cms-text`). This keeps the page truthful to the server snapshot
  // and never leaves orphaned typed text behind after an Exit.
  for (const [slot, entry] of staged.entries) {
    textCache.values.set(slot, entry.original);
    if (typeof document !== "undefined") {
      document.querySelectorAll(`[data-cms-auto-text="${CSS.escape(slot)}"]`).forEach((el) => {
        const fallback = el.dataset.cmsText || "";
        if (!fallback) return;
        const current = (el.textContent || "").replace(/\s+/g, " ").trim();
        if (current === fallback) return;
        if (el.childNodes.length === 1 && el.childNodes[0].nodeType === 3) {
          el.childNodes[0].data = fallback;
        } else {
          for (const n of el.childNodes) {
            if (n.nodeType === 3) { n.data = fallback; break; }
          }
        }
      });
    }
    notifyText(slot);
  }
  staged.entries.clear();
  notifyStaged();
};

export const commitStagedTexts = async () => {
  const items = Array.from(staged.entries.entries());
  if (!items.length) return { saved: 0 };
  staged.entries.clear();
  notifyStaged();
  let saved = 0;
  for (const [slot, entry] of items) {
    // Only push keys that actually differ from the server snapshot.
    const diff = {};
    for (const k of Object.keys(entry.current)) {
      if (entry.current[k] !== entry.original[k]) diff[k] = entry.current[k];
    }
    if (!Object.keys(diff).length) continue;
    try {
      await fetch(`${API}/api/text_slots/${encodeURIComponent(slot)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          values: diff,
          page: typeof window !== "undefined" ? window.location.pathname : null,
        }),
        cache: "no-store",
      });
      saved += 1;
      // Pull the auto-translated siblings shortly after.
      window.setTimeout(async () => {
        try {
          const res = await fetch(
            `${API}/api/text_slots/${encodeURIComponent(slot)}`,
            { cache: "no-store" }
          );
          if (!res.ok) return;
          const json = await res.json();
          const fresh = json?.values || {};
          const existing = textCache.values.get(slot) || {};
          textCache.values.set(slot, { ...existing, ...fresh });
          notifyText(slot);
        } catch (err) {
          devLog.debug("[CMS] refresh after translate failed", err?.message);
        }
      }, 2500);
    } catch (err) {
      console.error("[CMS] commit slot failed", slot, err);
    }
  }
  return { saved };
};

// ── Cross-cache reset ───────────────────────────────────────────────────
// Wipe every layer that caches CMS data on the client. Used after a
// "Mirror production DB" pull so the React app stops serving the
// pre-mirror in-memory snapshot and starts seeing the fresh documents
// the backend just inserted. Order matters: clear localStorage first
// (so a parallel persistToLocalStorage debounce can't restore stale
// data), then wipe the in-memory maps, then notify every subscriber
// so they re-render with empty state until the next ensure*Loaded()
// call repopulates.
export const resetSlotCaches = () => {
  try {
    if (typeof window !== "undefined") {
      window.localStorage.removeItem(LS_KEY);
      window.localStorage.removeItem(IMG_LS_KEY);
    }
  } catch {
    // private-mode / quota — ignore
  }

  // TEXT cache
  textCache.values.clear();
  textCache.ready = false;
  textCache.loading = null;
  for (const slot of textCache.subscribers.keys()) notifyText(slot);

  // IMAGE cache
  imgCache.values.clear();
  imgCache.ready = false;
  imgCache.loading = null;
  for (const slot of imgCache.subscribers.keys()) notifyImg(slot);
  for (const cb of imgCache.globalListeners) cb(null);
};

// ── IMAGE cache ─────────────────────────────────────────────────────────
const imgCache = {
  ready: false,
  loading: null,
  values: new Map(),               // slot_id → { url, cleared, alt_i18n }
  subscribers: new Map(),
  globalListeners: new Set(),      // fires on ANY slot change — used by AutoImageEditor
};

// ── Image slot synchronous hydration ────────────────────────────────
// Mirrors the text-slot pattern: hydrate `imgCache` at module load so
// <EditableImage> renders the override URL on its FIRST paint instead
// of showing the fallback and swapping to the CMS image once the
// async fetch resolves.
//
// Two sources, in priority order:
//   1. window.__CMS_INITIAL_IMAGES__ — populated by the index.html
//      boot script's parallel fetch of /api/slots. Freshest data,
//      fetched in the same round-trip as text.
//   2. localStorage snapshot of the previous visit's image overrides,
//      TTL 7 days. Zero-network first paint on returning visitors.
const IMG_LS_KEY = "xaluca-image-slots-v1";
const IMG_LS_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

const hydrateImgFromInitialState = () => {
  try {
    if (typeof window === "undefined") return 0;
    const initial = window.__CMS_INITIAL_IMAGES__;
    if (!initial || !Array.isArray(initial.slots)) return 0;
    let count = 0;
    for (const s of initial.slots) {
      if (s?.slot_id) {
        imgCache.values.set(s.slot_id, {
          url: s.url || null,
          cleared: !!s.cleared,
          alt_i18n: s.alt_i18n || null,
        });
        count += 1;
      }
    }
    if (count > 0) imgCache.ready = true;
    return count;
  } catch {
    return 0;
  }
};

const hydrateImgFromLocalStorage = () => {
  try {
    if (typeof window === "undefined") return 0;
    const raw = window.localStorage.getItem(IMG_LS_KEY);
    if (!raw) return 0;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.ts !== "number") return 0;
    if (Date.now() - parsed.ts > IMG_LS_MAX_AGE_MS) return 0;
    const slots = parsed.slots || {};
    let count = 0;
    for (const [slot, meta] of Object.entries(slots)) {
      if (meta && typeof meta === "object") {
        imgCache.values.set(slot, {
          url: meta.url || null,
          cleared: !!meta.cleared,
          alt_i18n: meta.alt_i18n || null,
        });
        count += 1;
      }
    }
    return count;
  } catch {
    return 0;
  }
};

let imgCachePersistTimer = null;
const persistImgToLocalStorage = () => {
  if (imgCachePersistTimer) clearTimeout(imgCachePersistTimer);
  imgCachePersistTimer = setTimeout(() => {
    try {
      if (typeof window === "undefined") return;
      const out = {};
      for (const [slot, meta] of imgCache.values) out[slot] = meta;
      const payload = JSON.stringify({ ts: Date.now(), slots: out });
      window.localStorage.setItem(IMG_LS_KEY, payload);
    } catch {
      // Quota exceeded / private-mode — silently skip.
    }
  }, 800);
};

// Run at module import time so the first React render sees the warm
// image cache. Prefer the in-flight fetch payload (freshest), fall
// back to the localStorage snapshot for returning visitors. If neither
// is available, `ensureImageLoaded` below awaits the still-pending fetch.
const imgInitialStateCount = hydrateImgFromInitialState();
if (imgInitialStateCount === 0) hydrateImgFromLocalStorage();

// Subscribe to ALL image-slot changes (used to re-apply DOM overrides).
export const subscribeAllImages = (cb) => {
  imgCache.globalListeners.add(cb);
  return () => imgCache.globalListeners.delete(cb);
};

const notifyImg = (slot) => {
  const subs = imgCache.subscribers.get(slot);
  if (subs) {
    const value = imgCache.values.get(slot);
    for (const cb of subs) cb(value);
  }
  for (const cb of imgCache.globalListeners) cb(slot);
};

export const ensureImageLoaded = () => {
  if (imgCache.ready) return Promise.resolve();
  if (imgCache.loading) return imgCache.loading;

  // Reuse the in-flight fetch kicked off by index.html's boot script.
  const earlyImgFetch =
    typeof window !== "undefined" ? window.__CMS_INITIAL_IMAGES_FETCH__ : null;
  if (earlyImgFetch && typeof earlyImgFetch.then === "function") {
    imgCache.loading = earlyImgFetch
      .then((json) => {
        const payload =
          json || window.__CMS_INITIAL_IMAGES__ || { slots: [] };
        for (const s of payload.slots || []) {
          if (s?.slot_id) {
            imgCache.values.set(s.slot_id, {
              url: s.url || null,
              cleared: !!s.cleared,
              alt_i18n: s.alt_i18n || null,
            });
          }
        }
        persistImgToLocalStorage();
      })
      .catch((err) => {
        devLog.debug("[CMS] image bulk load failed", err?.message);
      })
      .finally(() => {
        imgCache.ready = true;
        imgCache.loading = null;
        for (const slot of imgCache.subscribers.keys()) notifyImg(slot);
        for (const cb of imgCache.globalListeners) cb(null); // global ready signal
      });
    return imgCache.loading;
  }

  imgCache.loading = (async () => {
    try {
      const res = await fetch(`${API}/api/slots`, { cache: "no-store" });
      const json = await res.json();
      for (const s of json.slots || []) {
        if (s?.slot_id) {
          imgCache.values.set(s.slot_id, {
            url: s.url || null,
            cleared: !!s.cleared,
            alt_i18n: s.alt_i18n || null,
          });
        }
      }
      persistImgToLocalStorage();
    } catch (err) {
      devLog.debug("[CMS] image bulk load failed", err?.message);
    }
    imgCache.ready = true;
    imgCache.loading = null;
    for (const slot of imgCache.subscribers.keys()) notifyImg(slot);
    for (const cb of imgCache.globalListeners) cb(null); // global ready signal
  })();
  return imgCache.loading;
};

export const subscribeImage = (slot, cb) => {
  if (!imgCache.subscribers.has(slot)) imgCache.subscribers.set(slot, new Set());
  imgCache.subscribers.get(slot).add(cb);
  return () => imgCache.subscribers.get(slot)?.delete(cb);
};

export const getCachedImage = (slot) => {
  // Lazy sync-hydration: slotCache.js may have imported BEFORE the
  // boot-script fetch resolved (cold-cache path) — the initial
  // hydration passes at module load returned 0. Re-attempt on every
  // `getCachedImage` call until we get data. This runs during React's
  // synchronous first render (via `useState(() => getCachedImage(slot))`
  // in EditableImage), so if the fetch has completed by then we hit
  // the CMS image on the FIRST paint instead of after a useEffect swap.
  if (imgCache.values.size === 0 && !imgCache.ready) {
    if (hydrateImgFromInitialState() === 0) {
      hydrateImgFromLocalStorage();
    }
  }
  return imgCache.values.get(slot);
};

export const uploadSlotImage = async (slot, file) => {
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(
    `${API}/api/slots/${encodeURIComponent(slot)}/upload`,
    { method: "POST", body: fd, cache: "no-store" }
  );
  if (!res.ok) throw new Error(`Upload failed (${res.status})`);
  const json = await res.json();
  if (json?.url) {
    imgCache.values.set(slot, { url: json.url, cleared: false, alt_i18n: null });
    notifyImg(slot);
    persistImgToLocalStorage();
  }
  return json;
};

export const clearSlotImage = async (slot) => {
  await fetch(`${API}/api/slots/${encodeURIComponent(slot)}`, {
    method: "DELETE",
    cache: "no-store",
  });
  imgCache.values.set(slot, { url: null, cleared: true, alt_i18n: null });
  notifyImg(slot);
  persistImgToLocalStorage();
};

export const saveSlotImageMeta = async (slot, payload) => {
  // payload may include { alt_i18n, source, caption_i18n }
  const res = await fetch(`${API}/api/slots/${encodeURIComponent(slot)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    cache: "no-store",
  });
  if (!res.ok) throw new Error(`Save failed (${res.status})`);
  const json = await res.json();
  const current = imgCache.values.get(slot) || {};
  imgCache.values.set(slot, {
    url: json.url ?? current.url ?? null,
    cleared: !!json.cleared,
    alt_i18n: json.alt_i18n ?? current.alt_i18n ?? null,
  });
  notifyImg(slot);
  persistImgToLocalStorage();
  return json;
};
